import React, { createContext, useState, useCallback } from "react";
import type { ModalContextType, ModalConfig, ModalResult, ModalState } from "./types";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useAccessibility } from "../../modules/AccessibilityContext";

export const ModalContext = createContext<ModalContextType | null>(null);

interface ModalProviderProps {
  children: React.ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    config: null,
    resolve: null,
  });

  const showModal = useCallback((config: ModalConfig): Promise<ModalResult> => {
    return new Promise<ModalResult>((resolve) => {
      setModalState({
        isOpen: true,
        config,
        resolve,
      });
    });
  }, []);

  const closeModal = useCallback((result: ModalResult = "no") => {
    setModalState((prevState) => {
      if (prevState.resolve) {
        prevState.resolve(result);
      }
      return {
        isOpen: false,
        config: null,
        resolve: null,
      };
    });
  }, []);

  const contextValue: ModalContextType = {
    showModal,
    closeModal,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <ModalRenderer
        modalState={modalState}
        onYes={() => closeModal("yes")}
        onNo={() => closeModal("no")}
      />

    </ModalContext.Provider>
  );
};

// Modal renderer component
interface ModalRendererProps {
  modalState: ModalState;
  onYes: () => void;
  onNo: () => void;
}

const ModalRenderer: React.FC<ModalRendererProps> = ({
  modalState,
  onYes,
  onNo,
}) => {
  const { getAccessibilityClasses, getAccessibilityStyles } = useAccessibility();
  const { config, isOpen } = modalState;
  const yesButtonText = config?.yesButtonText || "Yes";
  const noButtonText = config?.noButtonText || "No";

  return (
    <Modal isOpen={isOpen} centered style={{ fontFamily: "NN Dagny Text Regular", ...getAccessibilityStyles() }}
      contentClassName={`${getAccessibilityClasses()}`} >

      <ModalHeader
        tag="h3"
        className={`text-orange`}
        cssModule={{ "modal-title": "w-100 text-center" }}
      >
        {config?.title}
      </ModalHeader>
      <ModalBody className="text-center" >
        {config?.message}
      </ModalBody>
      <ModalFooter className="text-center" style={{ justifyContent: 'center' }} >
        <Button className="btn-orange" onClick={onYes}>
          {yesButtonText}
        </Button>
        <Button className="btn-grey" onClick={onNo}>
          {noButtonText}
        </Button>
      </ModalFooter>
    </Modal >
  );
};

// Hook to use modal context
export const useModal = (): ModalContextType => {
  const context = React.useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
